import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { Collapse } from 'reactstrap';
import axios from 'axios';
import config from '../../config';
// Import Data
import { getMenuData } from "../LayoutMenuData";
import { usePermissions } from "../../Components/Hooks/PermissionHooks"; // Only using isAdmin now
import { useNotificationContext } from "../../contexts/NotificationContext";
import useRejectedDocumentsCount from "../../Components/Hooks/useRejectedDocumentsCount";
import useUnreadEmailsCount from "../../Components/Hooks/useUnreadEmailsCount";
//i18n
import { withTranslation } from "react-i18next";
import withRouter from "../../Components/Common/withRouter";
import { useSelector } from "react-redux";
import { createSelector } from 'reselect';
import { useState } from 'react';
import QuickMoneySpotlight from '../../Components/Common/QuickMoneySpotlight';

const VerticalLayout = (props : any) => {
    const { isAdmin, user } = usePermissions(); // Simplified - only need isAdmin and user
    const { unreadMessageCount } = useNotificationContext();
    const { count: rejectedDocumentsCount } = useRejectedDocumentsCount();
    const { count: unreadEmailsCount } = useUnreadEmailsCount();
    const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
    const [showQuickMoney, setShowQuickMoney] = useState(false);
    const [pendingApprovals, setPendingApprovals] = useState<number>(0);
    const [activePasswordOverrides, setActivePasswordOverrides] = useState<number>(0);
    const [pendingReviewsCount, setPendingReviewsCount] = useState<number>(0);
    const path = props.router.location.pathname;
    const lastFetchTimeRef = useRef<number>(0);
    
    // Get and filter menu data
    const getFilteredMenuData = () => {
        if (!user) return [];
        
        const rawMenuData = getMenuData();
        
        // Get menu permissions from user object
        const userMenuPermissions = user.menuPermissions || [];
        const isUserAdmin = user.level_id === 1;
        
        
        const filterMenuItems = (items: any[]): any[] => {
            // First pass: filter items based on permissions
            const filteredItems = items.map((item, index) => {
                // For headers, we need to check if the next items are visible
                if (item.isHeader) {
                    // Find next non-header items until the next header
                    let hasVisibleItems = false;
                    for (let i = index + 1; i < items.length; i++) {
                        if (items[i].isHeader) break;
                        
                        const nextItem = items[i];
                        // Check if this item would be visible
                        if (isAdmin() || isUserAdmin || 
                            (userMenuPermissions && userMenuPermissions.includes(nextItem.id))) {
                            hasVisibleItems = true;
                            break;
                        }
                    }
                    
                    return hasVisibleItems ? item : null;
                }
                
                return item;
            }).filter(Boolean);
            
            // Second pass: filter items based on permissions
            return filteredItems.filter(item => {

                // Headers are already filtered
                if (item.isHeader) {
                    return true;
                }

                // Special restriction for portfolio-reports: only visible to ct and st users
                if (item.id === 'portfolio-reports') {
                    return user.username === 'ct' || user.username === 'st';
                }

                // Admin sees everything (except portfolio-reports if not ct or st)
                if (isAdmin() || isUserAdmin) {
                    return true;
                }

                // Check menu permissions - if user has no menu permissions, hide all menu items
                if (!isAdmin() && !isUserAdmin) {
                    // If no menu permissions are defined or item is not in permissions, hide it
                    if (!userMenuPermissions || userMenuPermissions.length === 0 || !userMenuPermissions.includes(item.id)) {
                        return false;
                    }
                }

                // Permission checks removed - using only menuPermissions

                // Filter sub-items recursively
                if (item.subItems) {
                    const filteredSubItems = filterMenuItems(item.subItems);
                    // If no sub-items are visible, hide the parent too
                    if (filteredSubItems.length === 0) return false;
                    // Update the item with filtered sub-items
                    item.subItems = filteredSubItems;
                }

                return true;
            });
        };
        
        return filterMenuItems(rawMenuData);
    };
    
    const navData = getFilteredMenuData();
    
    const toggleItem = (itemId: string) => {
        setExpandedItems(prev => ({
            ...prev,
            [itemId]: !prev[itemId]
        }));
    };

    /*
 layout settings
 */

    const selectLayoutState = (state : any) => state.Layout;
    const selectLayoutProperties = createSelector(
        selectLayoutState,
        (layout) => ({
            leftsidbarSizeType: layout.leftsidbarSizeType,
            sidebarVisibilitytype: layout.sidebarVisibilitytype,
            layoutType: layout.layoutType
        })
    );
    // Inside your component
    const {
        leftsidbarSizeType, sidebarVisibilitytype, layoutType
    } = useSelector(selectLayoutProperties);

    //vertical and semibox resize events
    const resizeSidebarMenu = useCallback(() => {
        // Add null checks for document elements
        if (!document.documentElement || !document.body) {
            return;
        }
        
        var windowSize = document.documentElement.clientWidth;
        const humberIcon = document.querySelector(".hamburger-icon") as HTMLElement;
        var hamburgerIcon = document.querySelector(".hamburger-icon");
        
        // Cache attribute values to avoid multiple DOM accesses
        const dataLayout = document.documentElement ? document.documentElement.getAttribute("data-layout") : null;
        
        if (windowSize >= 1025) {
            if (dataLayout === "vertical" && document.documentElement) {
                document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType);
            }
            if (dataLayout === "semibox" && document.documentElement) {
                document.documentElement.setAttribute("data-sidebar-size", leftsidbarSizeType);
            }
            if ((sidebarVisibilitytype === "show" || layoutType === "vertical" || layoutType === "twocolumn") && document.querySelector(".hamburger-icon")) {
                if (hamburgerIcon !== null) {
                    hamburgerIcon.classList.remove("open");
                }
            } else {
                // var hamburgerIcon = document.querySelector(".hamburger-icon");
                if (hamburgerIcon !== null) {
                    hamburgerIcon.classList.add("open");
                }
            }

        } else if (windowSize < 1025 && windowSize > 767) {
            document.body.classList.remove("twocolumn-panel");
            if (dataLayout === "vertical" && document.documentElement) {
                document.documentElement.setAttribute("data-sidebar-size", "sm");
            }
            if (dataLayout === "semibox" && document.documentElement) {
                document.documentElement.setAttribute("data-sidebar-size", "sm");
            }
            if (humberIcon) {
                humberIcon.classList.add("open");
            }
        } else if (windowSize <= 767) {
            document.body.classList.remove("vertical-sidebar-enable");
            if (dataLayout !== "horizontal" && document.documentElement) {
                document.documentElement.setAttribute("data-sidebar-size", "lg");
            }
            if (humberIcon) {
                humberIcon.classList.add("open");
            }
        }
    }, [leftsidbarSizeType, sidebarVisibilitytype, layoutType]);

    useEffect(() => {
        window.addEventListener("resize", resizeSidebarMenu, true);
        return () => {
            window.removeEventListener("resize", resizeSidebarMenu, true);
        };
    }, [resizeSidebarMenu]);

    // Fetch pending approvals count
    const isUserAdmin = useMemo(() => isAdmin(), [isAdmin]); // Memoize the admin check
    
    useEffect(() => {
        if (!isUserAdmin) return;
        
        let isMounted = true;
        let isAuthorized = true;
        
        const fetchPendingApprovals = async () => {
            if (!isMounted || !isAuthorized) return;
            
            // Prevent rapid API calls - minimum 5 seconds between fetches
            const now = Date.now();
            if (now - lastFetchTimeRef.current < 5000) {
                return;
            }
            lastFetchTimeRef.current = now;
            
            try {
                const authUser = sessionStorage.getItem('authUser');
                let token = null;
                if (authUser) {
                    try {
                        const parsed = JSON.parse(authUser);
                        token = parsed.token || parsed.data?.token;
                    } catch (error) {
                        console.error('Error parsing authUser:', error);
                    }
                }

                const response = await axios.get(`${config.api.API_URL}/time-entries/flagged`, {
                    params: { page: 1, pageSize: 1 }, // We only need the total count
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                
                if (isMounted) {
                    setPendingApprovals(response.data.total || 0);
                }
            } catch (error: any) {
                // Stop polling if unauthorized
                if (error?.response?.status === 401) {
                    isAuthorized = false;
                }
                if (isMounted) {
                    console.error('Error fetching pending approvals:', error);
                }
            }
        };

        fetchPendingApprovals();
        // Refresh every 60 seconds
        const interval = setInterval(() => {
            if (isAuthorized) {
                fetchPendingApprovals();
            }
        }, 60000);
        
        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, [isUserAdmin]);

    // Fetch active password overrides
    useEffect(() => {
        let isMounted = true;

        const fetchPasswordOverrides = async () => {
            try {
                const authUser = sessionStorage.getItem('authUser');
                let token = null;
                if (authUser) {
                    try {
                        const parsed = JSON.parse(authUser);
                        token = parsed.token || parsed.data?.token;
                    } catch (error) {
                        console.error('Error parsing authUser:', error);
                    }
                }

                const response = await axios.get(`${config.api.API_URL}/dev-tools/users`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });

                if (isMounted && response.data?.activeOverrides !== undefined) {
                    setActivePasswordOverrides(response.data.activeOverrides);
                }
            } catch (error) {
                // Silently fail - this feature is only for admins
                console.debug('Could not fetch password overrides:', error);
            }
        };

        // Only fetch if user is admin
        if (isAdmin) {
            fetchPasswordOverrides();
            // Refresh every 30 seconds
            const interval = setInterval(fetchPasswordOverrides, 30000);

            return () => {
                isMounted = false;
                clearInterval(interval);
            };
        }
    }, [isAdmin]);

    // Fetch pending Frame.io reviews count
    useEffect(() => {
        let isMounted = true;

        const fetchPendingReviews = async () => {
            try {
                const authUser = sessionStorage.getItem('authUser');
                let token = null;
                if (authUser) {
                    try {
                        const parsed = JSON.parse(authUser);
                        token = parsed.token || parsed.data?.token;
                    } catch (error) {
                        console.error('Error parsing authUser:', error);
                    }
                }

                const response = await axios.get(`${config.api.API_URL}/frameio-workflow/my-reviews?status=pending`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });

                if (isMounted && response.data?.success) {
                    setPendingReviewsCount(response.data.data?.length || 0);
                }
            } catch (error) {
                // Silently fail
                console.debug('Could not fetch pending reviews:', error);
            }
        };

        fetchPendingReviews();
        // Refresh every 60 seconds
        const interval = setInterval(fetchPendingReviews, 60000);

        return () => {
            isMounted = false;
            clearInterval(interval);
        };
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        const initMenu = () => {
            const pathName = process.env.PUBLIC_URL + path;
            const ul = document.getElementById("navbar-nav") as HTMLElement;
            if (!ul) return; // Exit early if navbar-nav doesn't exist
            const items = ul.getElementsByTagName("a");
            let itemsArray = Array.from(items).filter(item => item instanceof HTMLAnchorElement) as HTMLAnchorElement[]; // converts NodeList to Array and ensures only HTMLAnchorElements
            removeActivation(itemsArray);
            let matchingMenuItem = itemsArray.find((x) => {
                return x.pathname === pathName;
            });
            if (matchingMenuItem) {
                activateParentDropdown(matchingMenuItem);
            }
            
            // Special handling for certain paths to expand parent menus
            if (path === '/receipts') {
                // Find and expand the invoices menu
                const invoicesMenu = itemsArray.find((x) => {
                    try {
                        // Ensure x is an HTMLElement before calling getAttribute
                        if (x && x instanceof HTMLElement && typeof x.getAttribute === 'function') {
                            return x.getAttribute('data-menu-key') === 'invoices';
                        }
                        return false;
                    } catch (e) {
                        return false;
                    }
                });
                if (invoicesMenu) {
                    const parentCollapse = invoicesMenu.closest('.nav-item')?.querySelector('.collapse');
                    if (parentCollapse) {
                        parentCollapse.classList.add('show');
                        invoicesMenu.classList.add('active');
                        invoicesMenu.setAttribute('aria-expanded', 'true');
                    }
                }
            }
            
            // Prevent tasks menu from activating on other pages
            if (path !== '/tasks' && !path.startsWith('/tasks/')) {
                // Ensure tasks menu is not active
                const tasksMenu = itemsArray.find((x) => {
                    try {
                        // Ensure x is an HTMLElement before calling getAttribute
                        if (x && x instanceof HTMLElement && typeof x.getAttribute === 'function') {
                            return x.getAttribute('data-menu-key') === 'tasks';
                        }
                        return false;
                    } catch (e) {
                        return false;
                    }
                });
                if (tasksMenu && tasksMenu.classList.contains('active')) {
                    tasksMenu.classList.remove('active');
                    const parentCollapse = tasksMenu.closest('.nav-item')?.querySelector('.collapse');
                    if (parentCollapse) {
                        parentCollapse.classList.remove('show');
                    }
                }
            }
        };
        if (props.layoutType === "vertical") {
            initMenu();
        }
    }, [path, props.layoutType]);

    function activateParentDropdown(item : any) {
        item.classList.add("active");
        let parentCollapseDiv = item.closest(".collapse.menu-dropdown");

        if (parentCollapseDiv) {
            // to set aria expand true remaining
            parentCollapseDiv.classList.add("show");
            parentCollapseDiv.parentElement.children[0].classList.add("active");
            parentCollapseDiv.parentElement.children[0].setAttribute("aria-expanded", "true");
            if (parentCollapseDiv.parentElement.closest(".collapse.menu-dropdown")) {
                parentCollapseDiv.parentElement.closest(".collapse").classList.add("show");
                if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling)
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.classList.add("active");
                if (parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse")) {
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").classList.add("show");
                    parentCollapseDiv.parentElement.closest(".collapse").previousElementSibling.closest(".collapse").previousElementSibling.classList.add("active");
                }
            }
            return false;
        }
        return false;
    }

    const removeActivation = (items :any) => {
        let actiItems = items.filter((x : any) => x.classList.contains("active"));

        actiItems.forEach((item : any) => {
            if (item.classList.contains("menu-link")) {
                if (!item.classList.contains("active")) {
                    item.setAttribute("aria-expanded", false);
                }
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
            }
            if (item.classList.contains("nav-link")) {
                if (item.nextElementSibling) {
                    item.nextElementSibling.classList.remove("show");
                }
                item.setAttribute("aria-expanded", false);
            }
            item.classList.remove("active");
        });
    };

    return (
        <React.Fragment>
            {/* menu Items */}
            {(navData || []).map((item : any, key : number) => {
                return (
                    <React.Fragment key={key}>
                        {/* Main Header */}
                        {item['isHeader'] ?
                            <li className={`menu-title ${item.headerClass || ''}`}><span data-key="t-menu">{props.t(item.label)} </span></li>
                            : (
                                (item.subItems ? (
                                    <li className="nav-item">
                                        <Link
                                            onClick={(e) => {
                                                // Always prevent default for expandable items
                                                if (item.isExpandable || item.link === "/#") {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleItem(item.id);
                                                }
                                            }}
                                            className="nav-link menu-link"
                                            to={item.link && item.link !== "/#" ? item.link : "#"}
                                            data-bs-toggle="collapse"
                                            data-menu-key={item.id}
                                            aria-expanded={expandedItems[item.id] || false}
                                        >
                                            <i className={item.icon}></i>
                                            <span data-key="t-apps">{props.t(item.label)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                        </Link>
                                        <Collapse
                                            className="menu-dropdown"
                                            isOpen={expandedItems[item.id] || false}
                                            id="sidebarApps">
                                            <ul className="nav nav-sm flex-column test">
                                                {/* subItms  */}
                                                {item.subItems && ((item.subItems || []).map((subItem : any, key : number) => (
                                                    <React.Fragment key={key}>
                                                        {!subItem.isChildItem ? (
                                                            <li className="nav-item">
                                                                <Link
                                                                    to={subItem.link ? subItem.link : "/#"}
                                                                    className="nav-link"
                                                                    data-menu-key={subItem.id}
                                                                    onClick={(e) => {
                                                                        if (subItem.onClick === 'quickMoney') {
                                                                            e.preventDefault();
                                                                            setShowQuickMoney(true);
                                                                        }
                                                                    }}
                                                                >
                                                                    {props.t(subItem.label)}
                                                                    {subItem.badgeName ?
                                                                        <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                        : null}
                                                                    {subItem.id === 'timeentryapprovals' && pendingApprovals > 0 && (
                                                                        <span className="badge badge-pill bg-danger ms-1">{pendingApprovals}</span>
                                                                    )}
                                                                    {subItem.id === 'frameio-my-reviews' && pendingReviewsCount > 0 && (
                                                                        <span className="badge badge-pill bg-danger ms-1">{pendingReviewsCount}</span>
                                                                    )}
                                                                </Link>
                                                            </li>
                                                        ) : (
                                                            <li className="nav-item">
                                                                <Link
                                                                    onClick={subItem.click}
                                                                    className="nav-link"
                                                                    to="/#"
                                                                    data-bs-toggle="collapse"
                                                                >
                                                                    {props.t(subItem.label)}
                                                                    {subItem.badgeName ?
                                                                        <span className={"badge badge-pill bg-" + subItem.badgeColor} data-key="t-new">{subItem.badgeName}</span>
                                                                        : null}
                                                                </Link>
                                                                <Collapse className="menu-dropdown" isOpen={subItem.stateVariables} id="sidebarEcommerce">
                                                                    <ul className="nav nav-sm flex-column">
                                                                        {/* child subItms  */}
                                                                        {subItem.childItems && (
                                                                            (subItem.childItems || []).map((childItem : any, key : number) => (
                                                                                <React.Fragment key={key}>
                                                                                    {!childItem.childItems ?
                                                                                        <li className="nav-item">
                                                                                            <Link
                                                                                                to={childItem.link ? childItem.link : "/#"}
                                                                                                className="nav-link">
                                                                                                {props.t(childItem.label)}
                                                                                            </Link>
                                                                                        </li>
                                                                                        : <li className="nav-item">
                                                                                            <Link to="/#" className="nav-link" onClick={childItem.click} data-bs-toggle="collapse">
                                                                                                {props.t(childItem.label)}
                                                                                            </Link>
                                                                                            <Collapse className="menu-dropdown" isOpen={childItem.stateVariables} id="sidebaremailTemplates">
                                                                                                <ul className="nav nav-sm flex-column">
                                                                                                    {childItem.childItems.map((subChildItem : any, key : number) => (
                                                                                                        <li className="nav-item" key={key}>
                                                                                                            <Link to={subChildItem.link} className="nav-link" data-key="t-basic-action">{props.t(subChildItem.label)} </Link>
                                                                                                        </li>
                                                                                                    ))}
                                                                                                </ul>
                                                                                            </Collapse>
                                                                                        </li>
                                                                                    }
                                                                                </React.Fragment>
                                                                            ))
                                                                        )}
                                                                    </ul>
                                                                </Collapse>
                                                            </li>
                                                        )}
                                                    </React.Fragment>
                                                ))
                                                )}
                                            </ul>

                                        </Collapse>
                                    </li>
                                ) : (
                                    <li className="nav-item">
                                        <Link
                                            className="nav-link menu-link"
                                            to={item.link ? item.link : "/#"}
                                            data-menu-key={item.id}>
                                            <i className={item.icon}></i> <span>{props.t(item.label)}</span>
                                            {item.badgeName ?
                                                <span className={"badge badge-pill bg-" + item.badgeColor} data-key="t-new">{item.badgeName}</span>
                                                : null}
                                            {item.id === 'messages' && unreadMessageCount > 0 && (
                                                <span className="badge badge-pill bg-danger ms-1">{unreadMessageCount > 99 ? '99+' : unreadMessageCount}</span>
                                            )}
                                            {item.id === 'mydocuments' && rejectedDocumentsCount > 0 && (
                                                <span className="badge badge-pill bg-danger ms-1">{rejectedDocumentsCount}</span>
                                            )}
                                            {item.id === 'emails' && unreadEmailsCount > 0 && (
                                                <span className="badge badge-pill bg-danger ms-1">{unreadEmailsCount}</span>
                                            )}
                                            {item.id === 'user-password-override' && activePasswordOverrides > 0 && (
                                                <span className="badge badge-pill bg-warning ms-1" title={`${activePasswordOverrides} aktive Password Overrides`}>
                                                    {activePasswordOverrides}
                                                </span>
                                            )}
                                        </Link>
                                    </li>
                                ))
                            )
                        }
                    </React.Fragment>
                );
            })}
            <QuickMoneySpotlight 
                isOpen={showQuickMoney}
                toggle={() => setShowQuickMoney(false)}
            />
        </React.Fragment>
    );
};

VerticalLayout.propTypes = {
    location: PropTypes.object,
    t: PropTypes.any,
};

export default withRouter(withTranslation()(VerticalLayout));